import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { TabProps, RechartsClickData } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import { useDrilldownAction, OTIFDrilldownModal, OTDDrilldownModal } from '../drilldown'
import { SectionTourTrigger } from '../tour/SectionTourTrigger'
import { CircularProgress, PeriodToggle, parseMonthToDateRange } from './SiteKPIComponents'
import { useQualityMeasureLabel } from '../../lib/qualityLabels'

// CR-3837: Extended props to support supplier type filter callback
interface SiteKPITabProps extends TabProps {
  onSupplierTypeChange?: (supplierType: string) => void;
}

export function SiteKPITab({ data, currency, filters, onSupplierTypeChange }: SiteKPITabProps) {
  const { formatCurrency } = useFormatters()
  const qualityLabel = useQualityMeasureLabel()

  // Delivery metric mode: conditionally show OTIF and/or OTD sections
  const deliveryMode = data.delivery_metric_mode ?? 'both'
  const showOTIF = deliveryMode === 'both' || deliveryMode === 'otif_only'
  const showOTD = deliveryMode === 'both' || deliveryMode === 'otd_only'

  const [activeSiteKPITab, setActiveSiteKPITab] = useState('delivery')
  // CR-3837: Use filter from props if available, with local state for UI
  const supplierFilter = filters?.supplierType || 'all'
  const [otifPeriod, setOtifPeriod] = useState('MONTH')
  const [spendPeriod, setSpendPeriod] = useState('MONTH')

  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()

  // OTIF specialized drill-down modal state
  const [isOTIFModalOpen, setIsOTIFModalOpen] = useState(false)
  const [otifModalContext, setOtifModalContext] = useState<string | undefined>(undefined)
  const [otifModalInitialTab, setOtifModalInitialTab] = useState<'failed' | 'arrears' | 'positive' | 'total' | undefined>(undefined)

  // OTD specialized drill-down modal state
  const [isOTDModalOpen, setIsOTDModalOpen] = useState(false)
  const [otdModalContext, setOtdModalContext] = useState<string | undefined>(undefined)
  const [otdModalInitialTab, setOtdModalInitialTab] = useState<'on_time' | 'early' | 'late' | 'total' | undefined>(undefined)

  // Convert dashboard filters to drilldown API format
  const drilldownFilters = {
    ...(filters?.dateFrom && { date_from: filters.dateFrom }),
    ...(filters?.dateTo && { date_to: filters.dateTo }),
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.site && { site: filters.site }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.supplierGroup && { supplier_group: filters.supplierGroup }),
    ...(filters?.supplierCountry && { supplier_country: filters.supplierCountry }),
  }

  // Handle chart click - open drill-down modal with month-specific data
  const handleChartClick = (chartData: RechartsClickData | null, metricType: 'ppm' | 'otif' | 'otd' | 'spend' | 'arrears' | 'ncrs' | 'appeals') => {
    if (!chartData) return
    let month: string | null = null
    if (chartData.activePayload && chartData.activePayload[0]) {
      month = (chartData.activePayload[0].payload?.['month'] as string) || (chartData.activePayload[0].payload?.['site'] as string) || null
    } else if (chartData.activeLabel) {
      month = chartData.activeLabel
    }
    if (!month) return

    // For OTIF, use the specialized OTIF drill-down modal
    if (metricType === 'otif') {
      setOtifModalContext(month)
      setOtifModalInitialTab(undefined)
      setIsOTIFModalOpen(true)
      return
    }

    // For OTD (depth of delivery), use the specialized OTD drill-down modal
    if (metricType === 'otd') {
      setOtdModalContext(month)
      setOtdModalInitialTab(undefined)
      setIsOTDModalOpen(true)
      return
    }

    // Parse month to date range for API filtering
    const dateRange = parseMonthToDateRange(month)
    const chartFilters = dateRange ? {
      ...drilldownFilters,
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
    } : drilldownFilters

    // Map metric types to drilldown keys
    const metricKeyMap: Record<string, string> = {
      'ppm': 'ppm',
      'spend': 'spend',
      'arrears': 'arrears',
      'ncrs': 'ncr',
      'appeals': 'appeals',
    }

    const metricKey = metricKeyMap[metricType]
    if (metricKey) {
      openDrilldown(metricKey, chartFilters, month)
    }
  }

  // Get site KPI data with defaults
  const siteKPIData = data.siteKPI || {
    otifBySite: [],
    ppmBySite: [],
    ncrBreakdown: [],
    deliveryBySite: [],
  }

  // OTIF values by period
  const otifValues = {
    MONTH: data.quality?.otif || 0,
    '3 MONTH': data.quality?.otif || 0,
    '12 MONTH': data.quality?.otif || 0,
  }

  // Spend values: sum from spend-by-month chart data for period accuracy
  const spendByMonthData: Array<{ month: string; spend: number }> = data.charts?.spendByMonth || []
  const spendFromChart = (months: number) => {
    const recent = spendByMonthData.slice(-months)
    return recent.reduce((sum, m) => sum + (m.spend || 0), 0)
  }
  const spendValues = {
    MONTH: spendByMonthData.length > 0 ? spendFromChart(1) : (data.cost?.totalSpend || 0),
    '3 MONTH': spendByMonthData.length > 0 ? spendFromChart(3) : (data.cost?.totalSpend || 0),
    '12 MONTH': spendByMonthData.length > 0 ? spendFromChart(12) : (data.cost?.totalSpend || 0),
  }

  // CR-3837: Use API data for appeals breakdown instead of hardcoded values
  const appealsData = data.delivery?.appealsBreakdown || {
    raised_by_customer: 0,
    raised_by_supplier: 0,
    accepted: 0,
    rejected: 0,
    auto_approved: 0,
  }

  // OTIF trend data - targets now come from API via SupplierPerformanceSettings
  const otifTrendData = data.charts?.otifData || []

  // CR-3837: Use API data for appeals trend instead of hardcoded values
  const appealsTrendData = data.charts?.appealsTrend || []

  // Spend trend data
  const spendTrendData = data.charts?.spendByMonth || []

  // Depth of delivery data
  const depthOfDeliveryData = siteKPIData.deliveryBySite

  // Arrears by lines data
  const arrearsByLinesData = data.charts?.arrearsDetails || []

  // Arrears by value data
  const arrearsByValueData = data.charts?.historicalArrears || []

  return (
    <div className="space-y-4">
      {/* Section Tour Trigger */}
      <div className="flex justify-end">
        <SectionTourTrigger
          pageKey="dashboard"
          sectionKey="site-kpi"
          variant="link"
          label="Tour this tab"
        />
      </div>

      {/* Supplier Filter */}
      <div className="bg-card rounded-lg shadow-sm p-4" data-tour="site-selector">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium">Suppliers:</span>
            <select
              value={supplierFilter}
              onChange={(e) => onSupplierTypeChange?.(e.target.value)}
              className="text-sm border rounded px-3 py-2 bg-background"
            >
              <option value="">All Suppliers</option>
              <option value="internal">Internal Suppliers</option>
              <option value="external">External Suppliers</option>
            </select>
          </div>
        </div>
      </div>

      {/* Sub-tabs for Delivery and Quality */}
      <div className="bg-card rounded-lg shadow-sm">
        <div className="border-b">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveSiteKPITab('delivery')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeSiteKPITab === 'delivery'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              Delivery
            </button>
            <button
              onClick={() => setActiveSiteKPITab('quality')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeSiteKPITab === 'quality'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              Quality
            </button>
          </nav>
        </div>
        <div className="p-6">
          {activeSiteKPITab === 'delivery' && (
            <div className="space-y-6">
              {/* KPI Metrics Row - OTIF, APPEALS, SPEND */}
              <div className={`grid gap-4 ${showOTIF ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {/* OTIF Card */}
                {showOTIF && (
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">OTIF</CardTitle>
                      <PeriodToggle
                        options={['MONTH', '3 MONTH', '12 MONTH']}
                        selected={otifPeriod}
                        onChange={setOtifPeriod}
                      />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center py-2">
                      <CircularProgress
                        value={otifValues[otifPeriod as keyof typeof otifValues]}
                        size={100}
                        strokeWidth={10}
                        color={otifValues[otifPeriod as keyof typeof otifValues] >= 80 ? '#10b981' : '#ef4444'}
                      />
                    </div>
                  </CardContent>
                </Card>
                )}

                {/* APPEALS Card - CR-3836: Improved alignment and visual layout */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">APPEALS</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="flex justify-between items-stretch gap-1">
                      <div className="flex-1 flex flex-col items-center justify-center p-2 bg-blue-50 rounded-md">
                        <div className="text-[10px] text-muted-foreground text-center leading-tight h-8 flex items-center">
                          Raised by<br/>Customer
                        </div>
                        <div className="text-xl font-bold text-blue-600 tabular-nums">{appealsData.raised_by_customer}</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center p-2 bg-purple-50 rounded-md">
                        <div className="text-[10px] text-muted-foreground text-center leading-tight h-8 flex items-center">
                          Raised by<br/>Supplier
                        </div>
                        <div className="text-xl font-bold text-purple-600 tabular-nums">{appealsData.raised_by_supplier}</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center p-2 bg-green-50 rounded-md">
                        <div className="text-[10px] text-muted-foreground text-center leading-tight h-8 flex items-center">
                          Accepted
                        </div>
                        <div className="text-xl font-bold text-green-600 tabular-nums">{appealsData.accepted}</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center p-2 bg-red-50 rounded-md">
                        <div className="text-[10px] text-muted-foreground text-center leading-tight h-8 flex items-center">
                          Rejected
                        </div>
                        <div className="text-xl font-bold text-red-600 tabular-nums">{appealsData.rejected}</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center p-2 bg-gray-50 rounded-md">
                        <div className="text-[10px] text-muted-foreground text-center leading-tight h-8 flex items-center">
                          Auto<br/>Approved
                        </div>
                        <div className="text-xl font-bold text-gray-600 tabular-nums">{appealsData.auto_approved}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* SPEND Card */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">SPEND</CardTitle>
                      <PeriodToggle
                        options={['MONTH', '3 MONTH', '12 MONTH']}
                        selected={spendPeriod}
                        onChange={setSpendPeriod}
                      />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center py-4">
                      <div className="text-2xl font-bold text-blue-600">
                        {formatCurrency(spendValues[spendPeriod as keyof typeof spendValues])}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row 1 - OTIF and Appeals */}
              <div className={`grid gap-4 ${showOTIF ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {showOTIF && (
                <ChartWrapper
                  title="OTIF GRAPH"
                  data={otifTrendData}
                  exportFilename="otif-graph"
                  legend={[
                    { color: '#10b981', label: 'OTIF' },
                    { color: '#ef4444', label: 'Target' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={otifTrendData}
                      onClick={(chartData) => handleChartClick(chartData, 'otif')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="otif" fill="#10b981" name="OTIF" radius={[4, 4, 0, 0]}>
                        {otifTrendData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.otif >= entry.target ? '#10b981' : '#ef4444'}
                          />
                        ))}
                      </Bar>
                      <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} dot={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
                )}

                <ChartWrapper
                  title="APPEALS GRAPH"
                  data={appealsTrendData}
                  exportFilename="appeals-graph"
                  legend={[
                    { color: '#3b82f6', label: 'Awaiting Customer' },
                    { color: '#10b981', label: 'Accepted' },
                    { color: '#f59e0b', label: 'Awaiting Supplier' },
                    { color: '#ef4444', label: 'Rejected' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={appealsTrendData}
                      onClick={(chartData) => handleChartClick(chartData, 'appeals')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="awaitingCustomer" stackId="a" fill="#3b82f6" name="Awaiting Customer" />
                      <Bar dataKey="accepted" stackId="a" fill="#10b981" name="Accepted" />
                      <Bar dataKey="awaitingSupplier" stackId="a" fill="#f59e0b" name="Awaiting Supplier" />
                      <Bar dataKey="rejected" stackId="a" fill="#ef4444" name="Rejected" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>

              {/* Charts Row 2 - Spend and Depth of Delivery */}
              <div className={`grid gap-4 ${showOTD ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <ChartWrapper
                  title="SPEND BY MONTH GRAPH"
                  data={spendTrendData}
                  exportFilename="spend-by-month"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart
                      data={spendTrendData}
                      onClick={(chartData) => handleChartClick(chartData, 'spend')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                      <Area
                        type="monotone"
                        dataKey="spend"
                        stroke="#3b82f6"
                        fill="#93c5fd"
                        name="Spend"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                {showOTD && (
                <ChartWrapper
                  title="DEPTH OF DELIVERY GRAPH"
                  data={depthOfDeliveryData}
                  exportFilename="depth-of-delivery"
                  legend={[
                    { color: '#10b981', label: 'On Time' },
                    { color: '#fbbf24', label: 'Early' },
                    { color: '#ef4444', label: 'Late' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={depthOfDeliveryData}
                      onClick={(chartData) => handleChartClick(chartData, 'otd')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="site" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="onTime" stackId="a" fill="#10b981" name="On Time" />
                      <Bar dataKey="early" stackId="a" fill="#fbbf24" name="Early" />
                      <Bar dataKey="late" stackId="a" fill="#ef4444" name="Late" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
                )}
              </div>

              {/* Charts Row 3 - Arrears by Lines and Arrears by Value */}
              <div className="grid grid-cols-2 gap-4">
                <ChartWrapper
                  title="ARREARS by LINES GRAPH"
                  data={arrearsByLinesData}
                  exportFilename="arrears-by-lines"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                      data={arrearsByLinesData}
                      onClick={(chartData) => handleChartClick(chartData, 'arrears')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Line
                        type="monotone"
                        dataKey="lines"
                        stroke="#ef4444"
                        strokeWidth={2}
                        dot={{ fill: '#ef4444', r: 4 }}
                        name="PO Lines"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title={`ARREARS ${currency} GRAPH`}
                  data={arrearsByValueData}
                  exportFilename="arrears-by-value"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                      data={arrearsByValueData}
                      onClick={(chartData) => handleChartClick(chartData, 'arrears')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={{ fill: '#3b82f6', r: 4 }}
                        name="Value"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>
            </div>
          )}

          {activeSiteKPITab === 'quality' && (
            <div className="space-y-6">
              {/* KPI Metrics Row - PPM, NCRs, Defect Rate */}
              <div className={`grid gap-4 ${showOTIF ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {/* PPM Card */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">{qualityLabel}</CardTitle>
                      <PeriodToggle
                        options={['MONTH', '3 MONTH', '12 MONTH']}
                        selected={otifPeriod}
                        onChange={setOtifPeriod}
                      />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center py-2">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-red-600">
                          {data.quality?.ppm?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Target: {data.quality?.ppmTarget?.toLocaleString() || 500}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* NCRs Card */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">NCRs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <div className="text-xs text-muted-foreground">Open</div>
                        <div className="text-lg font-bold text-amber-600">
                          {data.quality?.ncrsByStatus?.['open'] || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Closed</div>
                        <div className="text-lg font-bold text-green-600">
                          {data.quality?.ncrsByStatus?.['closed'] || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Overdue</div>
                        <div className="text-lg font-bold text-red-600">
                          {data.quality?.ncrsByStatus?.['overdue'] || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Total</div>
                        <div className="text-lg font-bold text-blue-600">
                          {data.quality?.ncrs || 0}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Defect Rate Card - derived from PPM */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">DEFECT RATE</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center py-2">
                      <CircularProgress
                        value={Math.min((data.quality?.ppm || 0) / 10000, 100)}
                        size={100}
                        strokeWidth={10}
                        color={data.quality?.ppm && data.quality.ppm > (data.quality?.ppmTarget || 500) ? '#ef4444' : '#10b981'}
                      />
                    </div>
                    <p className="text-xs text-center text-muted-foreground mt-1">
                      {((data.quality?.ppm || 0) / 10000).toFixed(2)}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row 1 - PPM Trend and NCR Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                <ChartWrapper
                  title={`${qualityLabel} TREND`}
                  data={data.charts?.ppmTrend || []}
                  exportFilename="ppm-trend"
                  legend={[
                    { color: '#ef4444', label: qualityLabel },
                    { color: '#3b82f6', label: 'Target' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={data.charts?.ppmTrend || []}
                      onClick={(chartData) => handleChartClick(chartData, 'ppm')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      {/* CR-4537: interval={0} forces every month tick to render on long ranges; angle + smaller font keep them readable */}
                      <XAxis dataKey="month" tick={{ fontSize: (data.charts?.ppmTrend || []).length > 24 ? 9 : 11 }} interval={0} angle={(data.charts?.ppmTrend || []).length > 12 ? -45 : 0} textAnchor={(data.charts?.ppmTrend || []).length > 12 ? 'end' : 'middle'} height={(data.charts?.ppmTrend || []).length > 12 ? 55 : 30} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="ppm" fill="#ef4444" name={qualityLabel} radius={[4, 4, 0, 0]}>
                        {(data.charts?.ppmTrend || []).map((entry, index: number) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.ppm <= entry.target ? '#10b981' : '#ef4444'}
                          />
                        ))}
                      </Bar>
                      <Line type="monotone" dataKey="target" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title="NCR BREAKDOWN < 12 MONTHS"
                  data={siteKPIData.ncrBreakdown}
                  exportFilename="ncr-breakdown"
                  legend={[
                    { color: '#fbbf24', label: 'Open' },
                    { color: '#10b981', label: 'Closed' },
                    { color: '#ef4444', label: 'Overdue' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={siteKPIData.ncrBreakdown}
                      onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="open" stackId="a" fill="#fbbf24" name="Open" />
                      <Bar dataKey="closed" stackId="a" fill="#10b981" name="Closed" />
                      <Bar dataKey="overdue" stackId="a" fill="#ef4444" name="Overdue" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>

              {/* Charts Row 2 - PPM by Site and NCR by Severity */}
              <div className="grid grid-cols-2 gap-4">
                <ChartWrapper
                  title={`${qualityLabel} BY SITE`}
                  data={siteKPIData.ppmBySite}
                  exportFilename="ppm-by-site"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={siteKPIData.ppmBySite}
                      onClick={(chartData) => handleChartClick(chartData, 'ppm')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="site" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="ppm" fill="#ef4444" name={qualityLabel} radius={[4, 4, 0, 0]}>
                        {(siteKPIData.ppmBySite || []).map((entry, index: number) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.ppm <= (entry.target || 500) ? '#10b981' : '#ef4444'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title="NCRs BY SEVERITY"
                  data={Object.entries(data.quality?.ncrsBySeverity || {}).map(([key, value]) => ({
                    severity: key.charAt(0).toUpperCase() + key.slice(1),
                    count: value,
                  }))}
                  exportFilename="ncrs-by-severity"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={Object.entries(data.quality?.ncrsBySeverity || {}).map(([key, value]) => ({
                        severity: key.charAt(0).toUpperCase() + key.slice(1),
                        count: value,
                      }))}
                      layout="vertical"
                      onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="severity" tick={{ fontSize: 11 }} width={80} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="count" name="NCRs" radius={[0, 4, 4, 0]}>
                        {Object.entries(data.quality?.ncrsBySeverity || {}).map(([key], index) => {
                          const colors: Record<string, string> = {
                            critical: '#ef4444',
                            major: '#f59e0b',
                            minor: '#3b82f6',
                            low: '#10b981',
                          }
                          return <Cell key={`cell-${index}`} fill={colors[key] || '#6b7280'} />
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>

              {/* Charts Row 3 - Live NCRs and Reject Value */}
              <div className="grid grid-cols-2 gap-4">
                <ChartWrapper
                  title="LIVE NCRs OVERDUE"
                  data={data.charts?.liveNCRsOverdue || []}
                  exportFilename="live-ncrs-overdue"
                  legend={[
                    { color: '#ef4444', label: 'Overdue' },
                    { color: '#f59e0b', label: 'Pending' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={data.charts?.liveNCRsOverdue || []}
                      onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="overdue" stackId="a" fill="#ef4444" name="Overdue" />
                      <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title="REJECT VALUE BY MONTH"
                  data={data.charts?.ppmNCRs?.slice(0, 6).map((ncr) => ({
                    month: ncr.date_raised?.substring(0, 7) || 'Unknown',
                    value: ncr.reject_value || 0,
                  })) || []}
                  exportFilename="reject-value-by-month"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart
                      data={data.charts?.ppmNCRs?.slice(0, 6).map((ncr) => ({
                        month: ncr.date_raised?.substring(0, 7) || 'Unknown',
                        value: ncr.reject_value || 0,
                      })) || []}
                      onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#ef4444"
                        fill="#fecaca"
                        name="Reject Value"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* OTIF Specialized Drill-Down Modal */}
      <OTIFDrilldownModal
        isOpen={isOTIFModalOpen}
        onClose={() => setIsOTIFModalOpen(false)}
        filters={drilldownFilters}
        contextLabel={otifModalContext}
        initialTab={otifModalInitialTab}
      />

      {/* OTD Specialized Drill-Down Modal */}
      <OTDDrilldownModal
        isOpen={isOTDModalOpen}
        onClose={() => setIsOTDModalOpen(false)}
        filters={drilldownFilters}
        contextLabel={otdModalContext}
        initialTab={otdModalInitialTab}
      />
    </div>
  )
}
